import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, ChevronUp, Copy, Loader2, Search } from 'lucide-react';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';

import CheckoutShell, { StatusMessage } from '../components/checkout/CheckoutShell';
import {
  fetchCheckoutIntent,
  fetchCheckoutIntentStatus,
  payCheckoutIntent,
  resetCheckoutIntent,
  verifyCheckoutTx,
  type CheckoutIntent,
  type MoneroPayResponse,
  type VerifyTxResponse,
} from '../lib/api';

const POLL_INTERVAL_MS = 5000;
const MONERO_ICON_SRC = '/icons/monero.svg';

type TxCheckState = 'idle' | 'checking' | VerifyTxResponse;

function moneroFromIntent(intent: CheckoutIntent): MoneroPayResponse | null {
  if (!intent.monero_address || !intent.monero_amount_xmr) {
    return null;
  }
  return {
    status: 'ok',
    address: intent.monero_address,
    amount_xmr: intent.monero_amount_xmr,
    payment_uri:
      intent.monero_payment_uri ??
      `monero:${intent.monero_address}?tx_amount=${intent.monero_amount_xmr}`,
    amount_fiat: intent.amount,
    currency: intent.currency,
    description: intent.description,
    invoice_ref: intent.invoice_ref,
  };
}

function truncateAddress(address: string, head = 14, tail = 10): string {
  if (address.length <= head + tail + 3) return address;
  return `${address.slice(0, head)}…${address.slice(-tail)}`;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-zt-border bg-zt-bg px-2 py-1 text-[11px] font-medium text-zt-muted transition hover:border-zt-primary/50 hover:text-slate-200"
      aria-label={`Copy ${label}`}
    >
      {copied ? (
        <Check className="h-3 w-3 text-zt-primary" aria-hidden="true" />
      ) : (
        <Copy className="h-3 w-3" aria-hidden="true" />
      )}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function parseMoneroPayResponse(data: Record<string, unknown>): MoneroPayResponse | null {
  if (data.status !== 'ok' || typeof data.address !== 'string' || !data.address) {
    return null;
  }
  return {
    status: 'ok',
    address: data.address,
    amount_xmr: typeof data.amount_xmr === 'string' ? data.amount_xmr : undefined,
    amount_fiat: typeof data.amount_fiat === 'string' ? data.amount_fiat : undefined,
    currency: typeof data.currency === 'string' ? data.currency : undefined,
    description: typeof data.description === 'string' ? data.description : null,
    invoice_ref: typeof data.invoice_ref === 'string' ? data.invoice_ref : null,
    payment_uri: typeof data.payment_uri === 'string' ? data.payment_uri : undefined,
    xmr_usd_rate: typeof data.xmr_usd_rate === 'string' ? data.xmr_usd_rate : undefined,
    error: typeof data.error === 'string' ? data.error : undefined,
  };
}

function resolveMoneroError(
  apiMessage: string | undefined,
  paymentStatus: string,
): { title: string; description: string } {
  const message = apiMessage ?? 'Could not create Monero payment address';
  const lower = message.toLowerCase();

  if (lower.includes('wallet rpc') || lower.includes('wallet unavailable') || lower.includes('rpc unavailable')) {
    return {
      title: 'Monero wallet unavailable',
      description: message,
    };
  }

  if (paymentStatus === 'processing') {
    return {
      title: 'Could not load Monero payment',
      description:
        'Your payment is in progress but the address could not be retrieved. Refresh this page or return to checkout and open Monero again.',
    };
  }

  if (lower.includes('not configured')) {
    return {
      title: 'Monero payments not configured',
      description: 'Monero checkout is not set up on this server yet.',
    };
  }

  return {
    title: 'Monero payment unavailable',
    description: message,
  };
}

function txCheckLabel(result: VerifyTxResponse): { tone: 'ok' | 'warn' | 'error' | 'muted'; text: string } {
  if (result.status === 'ok' && result.payment_status === 'paid') {
    return { tone: 'ok', text: 'Payment confirmed' };
  }
  if (result.status === 'pending' || result.payment_status === 'processing') {
    const conf = result.confirmations ?? 0;
    return {
      tone: 'warn',
      text: conf > 0 ? `Confirming (${conf} confirmation${conf === 1 ? '' : 's'})` : 'Seen in mempool — waiting for confirmations',
    };
  }
  if (result.status === 'not_configured') {
    return { tone: 'muted', text: result.message ?? 'Verification unavailable' };
  }
  const msg = (result.message ?? '').toLowerCase();
  if (msg.includes('amount') || msg.includes('match')) {
    return { tone: 'error', text: result.message ?? 'Wrong address or amount' };
  }
  return { tone: 'error', text: result.message ?? 'Transaction not found' };
}

const TONE_CLASSES = {
  ok: 'border-zt-primary/40 bg-zt-primary/10 text-zt-primary',
  warn: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  error: 'border-red-500/40 bg-red-500/10 text-red-300',
  muted: 'border-zt-border bg-zt-bg text-zt-muted',
} as const;

export default function MoneroCheckout() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [intent, setIntent] = useState<CheckoutIntent | null>(null);
  const [monero, setMonero] = useState<MoneroPayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ title: string; description: string } | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>('pending');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [txCheck, setTxCheck] = useState<TxCheckState>('idle');

  const checkoutPath = `/p/${token}`;

  const handlePaid = useCallback(
    (returnUrl?: string | null) => {
      toast.success('Payment received');
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        navigate(`${checkoutPath}?status=success`);
      }
    },
    [navigate, checkoutPath],
  );

  const initPayment = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setMonero(null);

    const intentRes = await fetchCheckoutIntent(token);
    if (!intentRes.ok || !intentRes.data) {
      setError({
        title: 'Payment link not found',
        description: intentRes.error?.message ?? 'This payment link is invalid or has expired.',
      });
      setLoading(false);
      return;
    }

    const intentData = intentRes.data;
    setIntent(intentData);
    setPaymentStatus(intentData.payment_status);

    if (intentData.payment_status === 'expired' || intentData.payment_status === 'paid') {
      setLoading(false);
      return;
    }

    const cached = moneroFromIntent(intentData);
    if (cached) {
      setMonero(cached);
      setLoading(false);
      return;
    }

    let payRes = await payCheckoutIntent(token, 'monero');
    if (
      !payRes.ok &&
      payRes.error?.message?.toLowerCase().includes('processing')
    ) {
      const resetRes = await resetCheckoutIntent(token);
      if (resetRes.ok && resetRes.data) {
        setIntent(resetRes.data);
        setPaymentStatus(resetRes.data.payment_status);
        payRes = await payCheckoutIntent(token, 'monero');
      }
    }
    if (!payRes.ok || !payRes.data) {
      setError(resolveMoneroError(payRes.error?.message, intentData.payment_status));
      setLoading(false);
      return;
    }

    const data = payRes.data;

    if (data.status === 'not_configured') {
      setError({
        title: 'Monero payments not configured',
        description: 'Monero checkout is not set up on this server yet.',
      });
      setLoading(false);
      return;
    }

    const moneroPayment = parseMoneroPayResponse(data);
    if (!moneroPayment) {
      const apiError =
        (typeof data.error === 'string' ? data.error : undefined) ??
        'Could not create Monero payment address';
      setError(resolveMoneroError(apiError, intentData.payment_status));
      setLoading(false);
      return;
    }

    setMonero(moneroPayment);
    if (intentData.payment_status === 'processing' || intentData.payment_status === 'pending') {
      setPaymentStatus(intentData.payment_status);
    }
    setLoading(false);
  }, [token]);

  const handleCheckTx = useCallback(async () => {
    const cleaned = txHash.trim();
    if (!cleaned) {
      toast.error('Paste your transaction ID (TXID)');
      return;
    }

    setTxCheck('checking');
    const res = await verifyCheckoutTx(token, cleaned);
    if (!res.ok || !res.data) {
      setTxCheck({
        status: 'not_found',
        payment_status: paymentStatus as VerifyTxResponse['payment_status'],
        message: res.error?.message ?? 'Could not verify transaction',
      });
      return;
    }

    setTxCheck(res.data);
    if (res.data.payment_status === 'paid') {
      handlePaid(intent?.return_url);
      return;
    }
    if (res.data.payment_status === 'processing') {
      setPaymentStatus('processing');
    }
  }, [txHash, token, paymentStatus, intent?.return_url, handlePaid]);

  useEffect(() => {
    document.title = 'Monero / XMR — ZeroTail';
    return () => {
      document.title = 'ZeroTail Dashboard';
    };
  }, []);

  useEffect(() => {
    void initPayment();
  }, [initPayment]);

  useEffect(() => {
    if (!monero) return;
    const uri = monero.payment_uri ?? `monero:${monero.address}`;
    void QRCode.toDataURL(uri, {
      width: 168,
      margin: 1,
      color: { dark: '#e2e8f0', light: '#0f172a00' },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [monero]);

  useEffect(() => {
    if (!token || !monero || paymentStatus === 'paid') return;

    let cancelled = false;

    const poll = async () => {
      const res = await fetchCheckoutIntentStatus(token);
      if (cancelled || !res.ok || !res.data) return;
      const status = res.data.payment_status;
      setPaymentStatus(status);
      if (status === 'paid') {
        handlePaid(intent?.return_url);
      }
    };

    void poll();
    const id = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token, monero, paymentStatus, intent?.return_url, handlePaid]);

  if (!token) {
    return (
      <CheckoutShell>
        <StatusMessage
          title="Payment link required"
          description="Open this page using the payment link you were sent. Contact whoever sent you the link if you need help."
        />
      </CheckoutShell>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-zt-muted">
        <Loader2 className="mr-2 h-6 w-6 animate-spin text-zt-primary" />
        Preparing Monero payment…
      </div>
    );
  }

  if (error) {
    return (
      <CheckoutShell compact>
        <StatusMessage title={error.title} description={error.description} />
        <p className="mt-4 text-center">
          <Link
            to={checkoutPath}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-zt-primary transition hover:text-zt-accent"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to checkout
          </Link>
        </p>
      </CheckoutShell>
    );
  }

  if (paymentStatus === 'paid') {
    return (
      <CheckoutShell compact>
        <StatusMessage
          icon={
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zt-primary/15 text-xl text-zt-primary ring-1 ring-zt-primary/30">
              ✓
            </div>
          }
          title="Payment received"
          description="Thank you. Your Monero payment has been confirmed."
        />
      </CheckoutShell>
    );
  }

  if (paymentStatus === 'expired' || (intent && intent.payment_status === 'expired')) {
    return (
      <CheckoutShell compact>
        <StatusMessage
          title="Payment expired"
          description="This checkout link is no longer available."
        />
        <p className="mt-4 text-center">
          <Link
            to={checkoutPath}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-zt-primary transition hover:text-zt-accent"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to checkout
          </Link>
        </p>
      </CheckoutShell>
    );
  }

  if (!intent || !monero) {
    return (
      <CheckoutShell compact>
        <StatusMessage
          title="Payment not found"
          description="This link is invalid or has expired."
        />
      </CheckoutShell>
    );
  }

  const txResult = txCheck !== 'idle' && txCheck !== 'checking' ? txCheck : null;
  const txLabel = txResult ? txCheckLabel(txResult) : null;

  return (
    <CheckoutShell compact>
      <div className="mb-3 flex items-center justify-between gap-2">
        <Link
          to={checkoutPath}
          className="inline-flex items-center gap-1 text-xs text-zt-muted transition hover:text-zt-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back
        </Link>
        <div className="flex items-center gap-2">
          <img src={MONERO_ICON_SRC} alt="" className="h-6 w-6" width={24} height={24} />
          <div className="text-right">
            <h1 className="text-sm font-semibold text-slate-100">Monero / XMR</h1>
            <p className="text-[10px] text-zt-muted">No 3rd parties, no email or JS required</p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-zt-border bg-zt-elevated px-3 py-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zt-muted">Amount due</p>
            <p className="text-lg font-semibold tabular-nums text-slate-100">
              {intent.currency} {intent.amount}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono text-base font-medium tabular-nums text-zt-primary">
              {monero.amount_xmr} XMR
            </p>
            {monero.xmr_usd_rate && (
              <p className="text-[11px] text-zt-muted">≈ ${monero.xmr_usd_rate}/XMR</p>
            )}
          </div>
        </div>
        {intent.description && (
          <p className="mt-1.5 truncate text-xs text-zt-muted" title={intent.description}>
            {intent.description}
          </p>
        )}
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[auto_1fr] sm:items-start">
        {qrDataUrl && (
          <div className="flex justify-center sm:justify-start">
            <div className="rounded-lg border border-zt-border bg-zt-bg p-2">
              <img
                src={qrDataUrl}
                alt="Monero payment QR code"
                className="h-[168px] w-[168px]"
                width={168}
                height={168}
              />
            </div>
          </div>
        )}

        <div className="min-w-0 space-y-2">
          <div>
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-zt-muted">
                Payment address
              </p>
              <button
                type="button"
                onClick={() => setAddressExpanded((v) => !v)}
                className="inline-flex items-center gap-0.5 text-[10px] text-zt-muted transition hover:text-zt-primary"
              >
                {addressExpanded ? (
                  <>
                    <ChevronUp className="h-3 w-3" aria-hidden="true" />
                    Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" aria-hidden="true" />
                    Full
                  </>
                )}
              </button>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-zt-border bg-zt-bg px-2.5 py-2">
              <p
                className={`min-w-0 flex-1 font-mono text-[11px] text-slate-200 ${
                  addressExpanded ? 'break-all leading-relaxed' : 'truncate'
                }`}
                title={monero.address}
              >
                {addressExpanded ? monero.address : truncateAddress(monero.address ?? '')}
              </p>
              <CopyButton value={monero.address ?? ''} label="Address" />
            </div>
          </div>

          <div>
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zt-muted">
              XMR amount
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-zt-border bg-zt-bg px-2.5 py-2">
              <p className="min-w-0 flex-1 font-mono text-sm tabular-nums text-slate-200">
                {monero.amount_xmr} XMR
              </p>
              <CopyButton value={monero.amount_xmr ?? ''} label="Amount" />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-zt-border bg-zt-elevated p-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zt-muted">
          Check transaction (TXID)
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCheckTx();
            }}
            placeholder="Paste transaction hash"
            className="min-w-0 flex-1 rounded-md border border-zt-border bg-zt-bg px-2.5 py-1.5 font-mono text-xs text-slate-200 placeholder:text-zt-muted/60 focus:border-zt-primary/50 focus:outline-none focus:ring-1 focus:ring-zt-primary/30"
            spellCheck={false}
            autoComplete="off"
          />
          <button
            type="button"
            onClick={() => void handleCheckTx()}
            disabled={txCheck === 'checking'}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-zt-primary px-3 py-1.5 text-xs font-medium text-zt-bg transition hover:bg-zt-accent disabled:opacity-60"
          >
            {txCheck === 'checking' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <Search className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            Check
          </button>
        </div>

        {txLabel && txResult && (
          <div className={`mt-2 rounded-md border px-2.5 py-2 text-xs ${TONE_CLASSES[txLabel.tone]}`}>
            <p className="font-medium">{txLabel.text}</p>
            {txResult.confirmations != null && txResult.confirmations > 0 && (
              <p className="mt-0.5 opacity-80">{txResult.confirmations} confirmation(s)</p>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center justify-center gap-2 rounded-md border border-zt-border/60 bg-zt-bg/50 px-3 py-2 text-xs text-zt-muted">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-zt-primary" aria-hidden="true" />
        <span>
          {paymentStatus === 'processing' ? 'Payment detected — confirming…' : 'Waiting for payment…'}
        </span>
      </div>

      <p className="mt-2 text-center text-[10px] leading-snug text-zt-muted">
        Send exactly the amount shown. Confirmation may take a few minutes.
      </p>
    </CheckoutShell>
  );
}
